import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Alert, Button, Form} from "react-bootstrap";
import {Link, useHistory, useLocation} from "react-router-dom";
import {useLoginInfoContext} from "./context/LoginInfoContext";

const componentName = "VerifyCode";
const OTP_LENGTH = 6;
const OTP_EXPIRY_SECONDS = 5 * 60;
const RESEND_LOCK_SECONDS = 60;
const RESEND_MESSAGE_DURATION_MS = 4000;
const MFA_ATTEMPT_STORAGE_PREFIX = "ce-mfa-attempts";
const MFA_INITIAL_CODE_STORAGE_PREFIX = "ce-mfa-initial-code-requested";

const formatTime = totalSeconds => {
  const safeSeconds = Math.max(0, totalSeconds);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}m ${seconds.toString().padStart(2, "0")}s`;
};

const toSecondsUntil = maybeDate => {
  if (!maybeDate) {
    return null;
  }
  const epoch = Date.parse(maybeDate);
  if (!Number.isFinite(epoch)) {
    return null;
  }
  return Math.max(0, Math.floor((epoch - Date.now()) / 1000));
};

const getInitialCodeStorageKey = challenge => {
  if (!challenge) {
    return null;
  }
  const username = challenge.username || challenge.email || "unknown";
  const token = challenge.mfaToken || challenge.mfa_token || "no-token";
  const source = challenge.selectedSource || challenge.sources?.[0] || "EMAIL";
  const attemptKey = `${MFA_ATTEMPT_STORAGE_PREFIX}:${username}:${source}:${token}`;
  return attemptKey ? attemptKey.replace(MFA_ATTEMPT_STORAGE_PREFIX, MFA_INITIAL_CODE_STORAGE_PREFIX) : null;
};

const VerifyCode = () => {
  const loginInfo = useLoginInfoContext();
  const history = useHistory();
  const location = useLocation();

  const [digits, setDigits] = useState(new Array(OTP_LENGTH).fill(""));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resendMessage, setResendMessage] = useState("");
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [maxFailedAttempts, setMaxFailedAttempts] = useState(1);
  const [failedRedirectSeconds, setFailedRedirectSeconds] = useState(null);
  const submitInFlightRef = useRef(false);
  const lastAutoSubmittedCode = useRef("");
  const initialCodeRequested = useRef(false);
  const inputsRef = useRef([]);

  const isPreviewMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("preview") === "1";
  }, [location.search]);

  const previewChallenge = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      requiresMfa: true,
      maskedEmail: params.get("email") || "a***@example.com",
      username: params.get("username") || "preview.user"
    };
  }, [location.search]);

  const activeChallenge = loginInfo.mfaChallenge || (isPreviewMode ? previewChallenge : null);

  const maskedEmail = activeChallenge?.maskedEmail
    || activeChallenge?.displayNames?.[0]
    || activeChallenge?.mfa_displayNames?.[0]
    || activeChallenge?.email;
  const expiryFromChallenge = toSecondsUntil(activeChallenge?.expiresAt);
  const resendFromChallenge = toSecondsUntil(activeChallenge?.resendAvailableAt);
  const initialCodeStorageKey = useMemo(
    () => getInitialCodeStorageKey(activeChallenge),
    [activeChallenge]
  );

  const [expirySeconds, setExpirySeconds] = useState(
    expiryFromChallenge ?? OTP_EXPIRY_SECONDS
  );
  const [resendLockSeconds, setResendLockSeconds] = useState(
    resendFromChallenge ?? RESEND_LOCK_SECONDS
  );
  const otpInputIds = useMemo(
    () => new Array(OTP_LENGTH).fill(null).map((_, idx) => `${componentName}-otp-${idx}`),
    []
  );
  const isLockedOut = failedAttempts >= maxFailedAttempts;

  useEffect(() => {
    if (!activeChallenge) {
      history.replace("/");
    }
  }, [activeChallenge, history]);

  useEffect(() => {
    if (!activeChallenge || isPreviewMode || initialCodeRequested.current) {
      return;
    }

    if (initialCodeStorageKey && sessionStorage.getItem(initialCodeStorageKey) === "true") {
      initialCodeRequested.current = true;
      return;
    }

    initialCodeRequested.current = true;
    if (initialCodeStorageKey) {
      sessionStorage.setItem(initialCodeStorageKey, "true");
    }
    setIsResending(true);
    setErrorMessage("");
    setResendMessage("");

    loginInfo.onResendOtp().then(([error]) => {
      setIsResending(false);
      if (error) {
        if (initialCodeStorageKey) {
          sessionStorage.removeItem(initialCodeStorageKey);
        }
        initialCodeRequested.current = false;
        setErrorMessage(extractErrorMessage(error));
        return;
      }
      setDigits(new Array(OTP_LENGTH).fill(""));
      lastAutoSubmittedCode.current = "";
      setExpirySeconds(OTP_EXPIRY_SECONDS);
      setResendLockSeconds(RESEND_LOCK_SECONDS);
      focusInput(0);
      setResendMessage("A verification code has been sent.");
    });
  }, [activeChallenge, initialCodeStorageKey, isPreviewMode, loginInfo]);

  useEffect(() => {
    if (expiryFromChallenge !== null) {
      setExpirySeconds(expiryFromChallenge);
    }
  }, [expiryFromChallenge]);

  useEffect(() => {
    if (resendFromChallenge !== null) {
      setResendLockSeconds(resendFromChallenge);
    }
  }, [resendFromChallenge]);

  useEffect(() => {
    if (!resendMessage) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setResendMessage("");
    }, RESEND_MESSAGE_DURATION_MS);

    return () => clearTimeout(timer);
  }, [resendMessage]);

  useEffect(() => {
    const timer = setInterval(() => {
      setExpirySeconds(prev => (prev > 0 ? prev - 1 : 0));
      setResendLockSeconds(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (failedRedirectSeconds === null) {
      return undefined;
    }
    if (failedRedirectSeconds <= 0) {
      loginInfo.setMfaChallenge(null);
      history.replace("/");
      return undefined;
    }
    const timer = setTimeout(() => {
      setFailedRedirectSeconds(prev => (prev === null ? null : prev - 1));
    }, 1000);
    return () => clearTimeout(timer);
  }, [failedRedirectSeconds, history, loginInfo]);

  const code = useMemo(() => digits.join(""), [digits]);

  const focusInput = index => {
    const target = inputsRef.current[index];
    if (target) {
      target.focus();
      target.select();
    }
  };

  const resetOtpEntry = useCallback(() => {
    setDigits(new Array(OTP_LENGTH).fill(""));
    lastAutoSubmittedCode.current = "";
    focusInput(0);
  }, []);

  const updateCodeWithString = (value, startIndex = 0) => {
    const sanitized = value.replaceAll(/\D/g, "");
    if (!sanitized) {
      return;
    }
    const nextDigits = [...digits];
    const maxLength = Math.min(OTP_LENGTH - startIndex, sanitized.length);
    for (let i = 0; i < maxLength; i += 1) {
      nextDigits[startIndex + i] = sanitized[i];
    }
    setDigits(nextDigits);
    setErrorMessage("");
    const nextFocus = Math.min(startIndex + maxLength, OTP_LENGTH - 1);
    focusInput(nextFocus);
  };

  const extractErrorMessage = err => {
    const status = err?.response?.status;
    if (status === 400) {
      return "The verification code you entered is incorrect. Please try again.";
    }
    return (
      err?.response?.data?.message ||
      err?.response?.data?.error_description ||
      err?.message ||
      "Unable to verify the code. Please try again."
    );
  };

  const submitCode = useCallback(async maybeCode => {
    const otpCode = maybeCode || code;
    if (
      otpCode.length !== OTP_LENGTH
      || isSubmitting
      || submitInFlightRef.current
      || expirySeconds <= 0
      || isLockedOut
    ) {
      return;
    }
    if (isPreviewMode) {
      setResendMessage("Preview mode: OTP submit is disabled.");
      return;
    }
    submitInFlightRef.current = true;
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const result = await loginInfo.onVerifyOtp(otpCode);
      if (result === true) {
        if (initialCodeStorageKey) {
          sessionStorage.removeItem(initialCodeStorageKey);
        }
        return;
      }

      if (result?.response?.status === 400) {
        resetOtpEntry();
        setFailedAttempts(result?.response?.data?.failedAttempts);
        setMaxFailedAttempts(result?.response?.data?.maxAttempts);
        if (result?.response?.data?.failedAttempts >= result?.response?.data?.maxAttempts) {
          return;
        }
        if (!isLockedOut) {
          setErrorMessage("Invalid or expired verification code. Please try again.");
        }
        return;
      }
      else if (result?.response?.status === 401 || result?.response?.status === 403) {
        setErrorMessage("Code has expired, please request a new one.");
        return;
      }

      setErrorMessage(extractErrorMessage(result));
    } finally {
      setIsSubmitting(false);
      submitInFlightRef.current = false;
    }
  }, [code, expirySeconds, initialCodeStorageKey, isLockedOut, isPreviewMode, isSubmitting, loginInfo, resetOtpEntry]);

  useEffect(() => {
    if (code.length === OTP_LENGTH && lastAutoSubmittedCode.current !== code) {
      lastAutoSubmittedCode.current = code;
      submitCode(code);
    }
  }, [code, submitCode]);

  return (
    <Form
      id={`${componentName}-form`}
      onSubmit={event => {
        event.preventDefault();
        submitCode();
      }}
    >
      <h2 id={`${componentName}-title`} className="mb-3">Verify Your Identity</h2>
      <p id={`${componentName}-subtitle`} className="mb-4">
        We've sent a 6-digit verification code to <strong>{maskedEmail || "your email"}</strong>
      </p>

      {failedAttempts > 0 && (
        <Alert id={`${componentName}-failedAttempts`} variant="warning">
          Failed attempts: {failedAttempts} of {maxFailedAttempts}
        </Alert>
      )}
      {errorMessage && (
        <Alert id={`${componentName}-error`} variant="danger">
          {errorMessage}
          {failedRedirectSeconds !== null && (
            <>
              {" "}
              <Link to="/">Back to login</Link>. Redirecting in {failedRedirectSeconds}s.
            </>
          )}
        </Alert>
      )}
      {resendMessage && (
        <Alert id={`${componentName}-resendMessage`} variant="success">
          {resendMessage}
        </Alert>
      )}

      <label className="mb-2" htmlFor={otpInputIds[0]}>Enter verification code</label>
      <div className="d-flex gap-2 mb-3" id={`${componentName}-otpInputs`}>
        {otpInputIds.map((otpInputId, idx) => (
          <Form.Control
            key={otpInputId}
            ref={element => {
              inputsRef.current[idx] = element;
            }}
            id={otpInputId}
            className="fs-4 p-2 text-center"
            inputMode="numeric"
            autoComplete={idx === 0 ? "one-time-code" : "off"}
            pattern="[0-9]*"
            maxLength={1}
            value={digits[idx]}
            disabled={isLockedOut}
            onChange={event => {
              const value = event.target.value.replaceAll(/\D/g, "");
              const nextDigits = [...digits];
              nextDigits[idx] = value.slice(-1);
              setDigits(nextDigits);
              setErrorMessage("");
              if (value && idx < OTP_LENGTH - 1) {
                focusInput(idx + 1);
              }
            }}
            onKeyDown={event => {
              if (event.key === "Backspace" && !digits[idx] && idx > 0) {
                focusInput(idx - 1);
              }
              if (event.key === "ArrowLeft" && idx > 0) {
                event.preventDefault();
                focusInput(idx - 1);
              }
              if (event.key === "ArrowRight" && idx < OTP_LENGTH - 1) {
                event.preventDefault();
                focusInput(idx + 1);
              }
            }}
            onPaste={event => {
              const pasted = event.clipboardData.getData("text").replaceAll(/\D/g, "");
              if (!pasted || pasted.length > OTP_LENGTH) {
                event.preventDefault();
                return;
              }
              event.preventDefault();
              updateCodeWithString(pasted, idx);
            }}
          />
        ))}
      </div>

      <p id={`${componentName}-expires`} className="mb-4">
        {expirySeconds > 0
          ? `Code expires in ${formatTime(expirySeconds)}`
          : "This code has expired. Request a new code."}
      </p>

      <Button
        id={`${componentName}-verifyButton`}
        type="submit"
        className="w-100 mb-4"
        disabled={isSubmitting || code.length !== OTP_LENGTH || expirySeconds <= 0 || isLockedOut}
      >
        {isSubmitting ? "Verifying..." : "Verify Code"}
      </Button>

      {!isLockedOut && (resendLockSeconds > 0 ? (
        <Button variant="secondary" id={`${componentName}-resendTimer`} className="w-100 text-center" disabled>
          Resend code available in {formatTime(resendLockSeconds)}
        </Button>
      ) : (
        <Button
          id={`${componentName}-resendButton`}
          variant="secondary"
          className="w-100 text-center"
          disabled={isResending || isLockedOut}
          onClick={async () => {
            setErrorMessage("");
            setResendMessage("");
            if (isPreviewMode) {
              setDigits(new Array(OTP_LENGTH).fill(""));
              lastAutoSubmittedCode.current = "";
              setExpirySeconds(OTP_EXPIRY_SECONDS);
              setResendLockSeconds(RESEND_LOCK_SECONDS);
              focusInput(0);
              setResendMessage("Preview mode: resend simulated.");
              return;
            }
            setIsResending(true);
            const [error] = await loginInfo.onResendOtp();
            setIsResending(false);
            if (error) {
              setErrorMessage(extractErrorMessage(error));
              return;
            }
            setDigits(new Array(OTP_LENGTH).fill(""));
            lastAutoSubmittedCode.current = "";
            setExpirySeconds(OTP_EXPIRY_SECONDS);
            setResendLockSeconds(RESEND_LOCK_SECONDS);
            focusInput(0);
            setResendMessage("A new verification code has been sent.");
          }}
        >
          {isResending ? "Resending..." : "Resend code"}
        </Button>
      ))}
      {isLockedOut && (
        <>
          <Alert id={`${componentName}-lockoutMessage`} variant="warning">
            Too many failed attempts. Go back to login and use Forgot Password to unlock your account.
          </Alert>
          <Button
            id={`${componentName}-backToLoginButton`}
            variant="secondary"
            className="w-100 text-center"
            onClick={() => {
              if (initialCodeStorageKey) {
                sessionStorage.removeItem(initialCodeStorageKey);
              }
              loginInfo.setMfaChallenge(null);
              history.replace("/");
            }}
          >
            Back to login
          </Button>
        </>
      )}
      {isPreviewMode && (
        <p className="text-center mt-3 mb-0">
          Preview mode is enabled.
        </p>
      )}
    </Form>
  );
};

export default VerifyCode;
